import { type FC, useCallback, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import type { AssetsSegmentElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { MlPersonByAssetAndVersionNullable } from '@iconik-io/iconik-api/src/services/ml';
import { Icon } from '@iconik-io/uad';

import Ellipsis from '../../../components/ellipsis/ellipsis';
import TriggerContextMenu from '../../../components/trigger_context_menu/trigger_context_menu';
import UserBubble from '../../../components/user_bubble/user_bubble';
import { selectIsTimeBasedAsset } from '../../../ducks/pages/asset/selectors';
import { setActivePerson } from '../../../ducks/panels/asset_face_recognition/slice';
import { confirmAssetVersionPerson } from '../../../ducks/panels/asset_face_recognition/thunks';
import { selectCurrentVersionId } from '../../../ducks/version/selectors';
import { useSegmentTrackEvent } from '../../../hooks/use_segment_track_event';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import Button from '../../../ui/button/button';
import IconButton from '../../../ui/icon_button/icon_button';
import type { MenuItem } from '../../../ui/menus/menu/types';
import { selectUserRoles } from '../../../utils/get_user_roles';
import RecognizedFaceContextMenu from './context_menu';
import { useScrollIntoView } from './hooks/use_scroll_into_view';
import PersonSelect from './person_select';
import { ToggleSegmentButton } from './segment_button';
import SegmentsList from './segments_list';

import styles from './recognized_persons.module.css';

type Props = {
  person: MlPersonByAssetAndVersionNullable;
  segments: AssetsSegmentElasticSchemaWithIdNullable[];
  highlighted: boolean;
  onPersonRowClick: (personId: string) => void;
  setSelectableItemIds?: (ids: string[]) => void;
};
const UnconfirmedPerson: FC<Props> = ({
  person,
  segments = [],
  highlighted,
  onPersonRowClick,
  setSelectableItemIds,
}) => {
  const [isSegmentsExpanded, setIsSegmentsExpanded] = useState(false);
  const [showNewPersonSelect, setShowNewPersonSelect] = useState(false);
  const [status, setStatus] = useState<'initial' | 'pending'>('initial');
  const dispatch = useAppDispatch();
  const userRoles = useAppSelector(selectUserRoles);
  const { id: assetId, versionId } = useParams({ strict: false });
  const unconfirmedPersonRowRef = useScrollIntoView(highlighted);
  const { trackSegmentEvent } = useSegmentTrackEvent();

  const isTimeBasedAsset = useAppSelector((state) =>
    selectIsTimeBasedAsset(state, assetId, versionId),
  );
  const shouldShowSegments = isTimeBasedAsset && segments.length > 0;
  const isSegmentsListVisible =
    isSegmentsExpanded && shouldShowSegments && !showNewPersonSelect;
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, versionId),
  );
  const highlightedSegmentIds = useAppSelector(
    (state) => state.segments.highlightedSegmentIds,
  );

  const handleMenuItemClick = useCallback((option: MenuItem) => {
    if (option.id === 'change_person') {
      setShowNewPersonSelect(true);
    }
  }, []);

  const handleConfirmMatchClick = () => {
    if (!currentVersionId || !assetId) {
      return;
    }
    setStatus('pending');
    dispatch(setActivePerson(undefined));
    setSelectableItemIds?.([]);
    dispatch(confirmAssetVersionPerson(person.id, assetId, currentVersionId))
      .then(() => {
        trackSegmentEvent('Confirmed Person', {
          Place: 'panel',
          Profile: person.name ? 'existing' : 'new',
        });
      })
      .finally(() => {
        setStatus('initial');
      });
  };

  return (
    <div
      data-id={person.id}
      ref={unconfirmedPersonRowRef}
      className={classNames(styles.person_item, styles.unconfirmed_person)}
      aria-label="Recognized Person - Unconfirmed"
    >
      <div
        className={classNames(styles.person_row, {
          [styles.highlighted]: highlighted,
        })}
        onClick={() => onPersonRowClick(person.id)}
      >
        <div className={styles.person_row_inner}>
          <UserBubble
            className={classNames(
              styles.unconfirmed_person_image,
              styles.person_avatar,
            )}
            size="md"
            variant="square"
            name=""
            src={person.person_version_face_image_url ?? undefined}
          />
          <Icon name="move" size="xs" className={styles.icon} />
          <UserBubble
            className={styles.person_avatar}
            size="md"
            name={person.name ?? ''}
            src={person.image_url ?? undefined}
          />
          <div className={styles.unconfirmed_person_name_column}>
            <div className={styles.name}>
              <Ellipsis>{person.name ? person.name : null}</Ellipsis>
            </div>

            <div
              className={styles.person_row_inner}
              onClick={(event) => {
                event.stopPropagation();
                dispatch(setActivePerson(undefined));
              }}
            >
              {shouldShowSegments ? (
                <ToggleSegmentButton
                  isExpanded={isSegmentsExpanded}
                  onChange={setIsSegmentsExpanded}
                >
                  {segments.length}
                </ToggleSegmentButton>
              ) : null}
              <Button
                iconName="loader_circle_done_legacy"
                size="s"
                disabled={!userRoles.canAddPerson || status === 'pending'}
                onClick={handleConfirmMatchClick}
              >
                Confirm match
              </Button>
            </div>
          </div>
        </div>
        <div
          onClick={(event) => {
            event.stopPropagation();
            dispatch(setActivePerson(undefined));
          }}
        >
          <TriggerContextMenu
            triggerElement={
              <IconButton
                onClick={() => {
                  setSelectableItemIds?.([person.id]);
                }}
                iconName="menu_context"
                aria-label="Open person actions menu"
              />
            }
            contextMenu={RecognizedFaceContextMenu}
            contextItems={[{ ...person }]}
            onMenuItemClick={handleMenuItemClick}
          />
        </div>
      </div>
      {isSegmentsListVisible ? (
        <SegmentsList
          highlightedSegmentIds={highlightedSegmentIds}
          person={person}
          segments={segments}
        />
      ) : null}
      {showNewPersonSelect ? (
        <PersonSelect
          segments={segments}
          onSetShowPersonSelect={setShowNewPersonSelect}
          onDeactivate={() => {
            dispatch(setActivePerson(undefined));
            setSelectableItemIds?.([]);
          }}
          person={person}
          actionType="change"
          rowType="unconfirmed"
        />
      ) : null}
    </div>
  );
};

export default UnconfirmedPerson;
